import {useMutation, useSuspenseQuery} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {isPossiblePhoneNumber, parsePhoneNumberFromString} from "libphonenumber-js";
import {useState} from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {FiAlertCircle, FiCheck, FiPlus} from "react-icons/fi";
import {LuBellRing} from "react-icons/lu";
import * as yup from "yup";

import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {PhoneField} from "@/components/form/fields/PhoneField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {
  ContactGroupsListResult,
  GQL_CONTACT_GROUPS_LIST_QUERY,
  GQL_DELETE_CONTACT_GROUP,
  GQL_TEST_CONTACT_GROUP,
  GQL_UPDATE_CONTACT_GROUP,
  UpdateContactGroupResult,
} from "@/lib/queries/contacts.ts";
import {ScheduleState} from "@/lib/types/checks.ts";
import {GraphQLResponse, QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {ButtonRow} from "@/pages/onboardingwizard/components/buttonrow/ButtonRow.tsx";
import {StepBodyProps} from "@/pages/onboardingwizard/components/step/Step.tsx";
import {getLocalData, setLocalData} from "@/pages/onboardingwizard/utils.ts";

import styles from "./StepAlertsBody.module.scss";

const minRows = 2;
const maxRows = 5;

type Row = {
  name: string;
  email: string;
  sms: string;
  contactId?: number;
  contactGroup?: ContactGroup;
  testedEmail?: string;
  testedSms?: string;
};

type FormValues = {
  rows: Row[];
};

type RowKeys = keyof Row;

const defaultRow: Row = {name: "", email: "", sms: ""};

const FormSchema = yup.object({
  rows: yup
    .array()
    .of(
      yup.object().shape(
        {
          name: yup.string().required("Name is required"),
          email: yup
            .string()
            .default("")
            .email("Invalid email address")
            .when("sms", {
              is: (sms: string) => !sms || sms.length <= 4,
              then: (schema) => schema.required("Email or phone number is required"),
            }),
          sms: yup
            .string()
            .default("")
            .test(
              "is-valid-phone",
              "${value} must be a valid phone number in international format (Ex: +442012341234).",
              (value) => !value || isPossiblePhoneNumber(value)
            )
            .when("email", {
              is: (email: string) => !email,
              then: (schema) => schema.required("Email or phone number is required"),
            }),
        },
        [["email", "sms"]]
      )
    )
    .default([{...defaultRow}]),
});

export const StepAlertsBody = ({onSubmit, stepsCount, activeStepNr}: StepBodyProps) => {
  const {user} = useCurrentUserContext();
  const {
    data: {
      contactGroups: {items},
    },
    refetch,
  } = useSuspenseQuery<ContactGroupsListResult>(GQL_CONTACT_GROUPS_LIST_QUERY, {
    variables: {
      limit: 5,
      offset: 0,
      sort: "name",
    },
  });
  const [updateContact] = useMutation<UpdateContactGroupResult>(GQL_UPDATE_CONTACT_GROUP);
  const [deleteContact] = useMutation<GraphQLResponse<null>>(GQL_DELETE_CONTACT_GROUP);
  const [testContactGroup] = useMutation<GraphQLResponse<null>>(GQL_TEST_CONTACT_GROUP);
  const [emailsTested, setEmailsTested] = useState<string[]>([]);
  const [smsTested, setSmsTested] = useState<string[]>([]);

  const getDefaultContacts = () => {
    if (user) {
      const localData = getLocalData(user.id, activeStepNr);
      if (localData) {
        return localData;
      }
    }
    const defaultContacts: FormValues["rows"] = items
      .filter(({email_list, sms_list}) => email_list.length > 0 || sms_list.length > 0)
      .map((contact) => {
        // Original object and lists are frozen and sealed
        // The contact is cloned to be able to delete elements from the lists
        const contactGroup = {...contact, email_list: [...contact.email_list], sms_list: [...contact.sms_list]};
        // The email and sms are removed from the contact lists
        // to act as an update when adding a new email or sms
        // instead of adding another email or sms to the list when edited
        const email = contactGroup.email_list.pop() ?? "";
        const sms = contactGroup.sms_list.pop() ?? "";

        return {
          name: contact.name,
          email: email,
          sms: sms,
          contactId: contact.id,
          contactGroup: contactGroup,
        };
      });

    while (defaultContacts.length < maxRows) {
      defaultContacts.push({...defaultRow});
      if (defaultContacts.length >= minRows) {
        break;
      }
    }

    return defaultContacts;
  };

  const methods = useForm<FormValues>({
    defaultValues: {
      rows: getDefaultContacts(),
    },
    resolver: yupResolver<FormValues>(FormSchema),
  });
  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: {errors},
  } = methods;
  const {fields, append} = useFieldArray({
    control,
    name: "rows",
  });

  const addRow = () => {
    if (fields.length < maxRows) {
      append({...defaultRow});
    }
  };

  const showRowErrors = (rowIdx: number, response: QueryError) => {
    const fieldNames: RowKeys[] = ["name", "email", "sms"];

    fieldNames.forEach((fieldName) => {
      if (response.message[fieldName]) {
        setError(`rows.${rowIdx}.${fieldName}`, {
          type: "manual",
          message: response.message[fieldName][0],
        });
      }
    });
  };

  const getRowData = (row: Row) => {
    const emailList = row.email ? [row.email] : [];
    const smsList = row.sms.length > 4 ? [row.sms] : [];

    if (row.contactGroup) {
      return {
        id: row.contactGroup.id,
        name: row.name || row.contactGroup.name,
        email_list: Array.from(new Set([...row.contactGroup.email_list, ...emailList])),
        sms_list: Array.from(new Set([...row.contactGroup.sms_list, ...smsList])),
        up_alerts_email_only: row.contactGroup.up_alerts_email_only,
        phonecall_list: row.contactGroup.phonecall_list,
        // rows are restored from local storage for 24h, so one saved by a bundle that
        // predates this field has none and submitRow would read .length of undefined
        whatsapp_list: row.contactGroup.whatsapp_list ?? [],
        integrations: row.contactGroup.integrations,
        push_notification_profiles: row.contactGroup.push_notifications ?? [],
        schedule_entries: row.contactGroup.schedule_entries,
        schedule_state: row.contactGroup.schedule_state,
      };
    } else {
      return {
        id: row.contactId,
        name: row.name,
        email_list: emailList,
        sms_list: smsList,
        up_alerts_email_only: false,
        phonecall_list: [],
        whatsapp_list: [],
        integrations: [],
        push_notification_profiles: [],
        schedule_entries: [],
        schedule_state: ScheduleState.ACTIVE,
      };
    }
  };

  const submitRow = async (row: Row) => {
    const rowData = getRowData(row);

    if (
      rowData.email_list.length === 0 &&
      rowData.sms_list.length === 0 &&
      rowData.phonecall_list.length === 0 &&
      rowData.whatsapp_list.length === 0 &&
      rowData.integrations.length === 0 &&
      rowData.push_notification_profiles.length === 0
    ) {
      return deleteContact({
        variables: {
          id: rowData.id,
        },
      });
    } else {
      return updateContact({
        variables: {
          data: rowData,
        },
      });
    }
  };

  const sendTest = async (index: number) => {
    clearErrors(`rows.${index}`);
    const isValid = await trigger([`rows.${index}.name`, `rows.${index}.email`, `rows.${index}.sms`]);

    if (isValid) {
      const rows = getValues("rows");
      const row = rows[index];
      const result = await submitRow(row);

      if (result.data?.response.status === ResponseStatus.ERROR) {
        const response = result.data.response as QueryError;
        showRowErrors(index, response);
      } else if (result.data?.response.status === ResponseStatus.SUCCESS) {
        const contactId = result.data?.response.data?.id;
        setValue(`rows.${index}.contactId`, contactId);

        const testResult = await testContactGroup({
          variables: {
            id: contactId,
          },
        });

        if (testResult.data?.response.status === ResponseStatus.SUCCESS) {
          setValue(`rows.${index}.testedEmail`, row.email);
          setValue(`rows.${index}.testedSms`, row.sms.length > 4 ? row.sms : "");

          const {emailsTested, smsTested} = rows.reduce(
            (result, {testedEmail, testedSms}) => {
              if (testedEmail) {
                result.emailsTested.add(testedEmail);
              }
              if (testedSms) {
                result.smsTested.add(testedSms);
              }
              return result;
            },
            {emailsTested: new Set<string>(), smsTested: new Set<string>()}
          );
          setEmailsTested(Array.from(emailsTested));
          setSmsTested(Array.from(smsTested).map((sms) => parsePhoneNumberFromString(sms)?.formatNational() ?? sms));
        } else {
          console.error("Failed to send test:", testResult.data?.response.message);
        }
      }
    }
  };

  const submitStep = async (back?: boolean) => {
    const allRows = getValues().rows;
    setLocalData(user?.id, activeStepNr, back ? allRows : undefined);
    if (back) {
      return onSubmit(activeStepNr - 1, true);
    }
    const rowsToBeSaved = allRows
      .map((row, idx) => ({...row, idx})) // Add index to each row to keep track of the original row number
      .filter((row) => row.name !== "" || row.email !== "" || row.sms.length > 4 || row.contactId);

    let isValid = true;

    // Validate only non-empty rows
    for (const row of rowsToBeSaved) {
      clearErrors(`rows.${row.idx}`);

      if (rowIsEmpty(row.idx)) {
        continue;
      }

      const valid = await trigger(`rows.${row.idx}`);
      if (!valid) {
        isValid = false;
      }
    }
    if (!isValid) {
      return;
    }

    // Save all in parallel
    const results = await Promise.all(rowsToBeSaved.map((row) => submitRow(row)));

    let hasErrors = false;
    let hasSuccess = false;

    results.forEach((result, idx) => {
      if (result.data?.response.status === ResponseStatus.ERROR) {
        hasErrors = true;
        const response = result.data.response as QueryError;
        showRowErrors(rowsToBeSaved[idx].idx, response);
      } else if (result.data?.response.status === ResponseStatus.SUCCESS) {
        hasSuccess = true;
        clearErrors(`rows.${rowsToBeSaved[idx].idx}`);
        const contactId = result.data?.response.data?.id;
        setValue(`rows.${rowsToBeSaved[idx].idx}.contactId`, contactId);
      }
    });

    if (hasSuccess) {
      refetch();
    }

    if (!hasErrors) {
      onSubmit(activeStepNr + 1, true);
    }
  };

  const rowIsEmpty = (rowIdx: number, skipName?: boolean) => {
    const row = getValues(`rows.${rowIdx}`);
    let rowHasValues = !!row.email || !!row.sms;
    if (!skipName) {
      rowHasValues = rowHasValues || !!row.name;
    }

    return !rowHasValues;
  };

  const handleBlur = (rowIdx: number, fieldName: RowKeys) => {
    if (rowIsEmpty(rowIdx)) {
      return;
    }
    if (fieldName === "name") {
      trigger(`rows.${rowIdx}.${fieldName}`);
    } else {
      trigger(`rows.${rowIdx}`);
    }
  };

  const handleChange = (rowIdx: number, fieldName: RowKeys) => {
    if (!errors.rows?.[rowIdx]) {
      return;
    }
    if (rowIsEmpty(rowIdx)) {
      clearErrors(`rows.${rowIdx}`);
    } else {
      clearErrors(`rows.${rowIdx}.${fieldName}`);
    }
  };

  return (
    <div className="pulse-color-gray-900">
      <FormProvider {...methods}>
        <form id="onboarding-alerts-form">
          <div className={styles.contactsWrapper}>
            <div className={styles.contactsGroup}>
              {fields.map((field, index) => (
                <div key={field.id} className={styles.contactRowWrapper}>
                  <div className={styles.contactRow}>
                    <InputField
                      className="w-100"
                      name={`rows.${index}.name`}
                      label="Full Name"
                      placeholder="Full Name"
                      options={{
                        onChange: () => handleChange(index, "name"),
                        onBlur: () => handleBlur(index, "name"),
                      }}
                      hideErrorMessage
                    />
                    <InputField
                      className="w-100"
                      type="email"
                      name={`rows.${index}.email`}
                      label="Email"
                      placeholder="Email Address"
                      options={{
                        onChange: () => handleChange(index, "email"),
                        onBlur: () => handleBlur(index, "email"),
                      }}
                      hideErrorMessage
                    />
                    <PhoneField
                      className="w-100"
                      name={`rows.${index}.sms`}
                      label="Phone"
                      placeholder="Phone (optional - for alerts)"
                      disableDialCodeAndPrefix={false}
                      onChange={() => handleChange(index, "sms")}
                      onBlur={() => handleBlur(index, "sms")}
                      hideErrorMessage
                    />
                    <Button
                      className={styles.sendTestButton}
                      variant="secondary"
                      onClick={() => sendTest(index)}
                      disabled={rowIsEmpty(index, true) || !!errors.rows?.[index]}
                    >
                      <Icon icon={LuBellRing} /> Send Test
                    </Button>
                  </div>
                  {index < fields.length - 1 && <Divider className={styles.divider} />}
                </div>
              ))}
            </div>
            <div className={styles.addButtonMobile}>
              {fields.length < maxRows ? (
                <Button variant="secondary" onClick={addRow}>
                  <Icon icon={FiPlus} /> Add Another
                </Button>
              ) : (
                <div className={styles.info}>To setup additional alerts, go to Notifications &gt; Contacts.</div>
              )}
            </div>
            {errors.rows?.length && (
              <div className={styles.error}>
                <Icon icon={FiAlertCircle} className="me-1" display="inline" removeDivDimensions />
                Invalid data provided - check highlighted fields.
              </div>
            )}
            {(emailsTested.length > 0 || smsTested.length > 0) && (
              <div className={styles.results}>
                {emailsTested.length > 0 && (
                  <div className="pulseTextBodySmall">
                    <Icon icon={FiCheck} className="pulse-color-green-500 me-1" display="inline" removeDivDimensions />
                    <span className="pulse-color-gray-600">Test email sent to </span>
                    <span className="pulse-color-blue-500">{emailsTested.join(", ")}</span>
                  </div>
                )}
                {smsTested.length > 0 && (
                  <div className="pulseTextBodySmall">
                    <Icon icon={FiCheck} className="pulse-color-green-500 me-1" display="inline" removeDivDimensions />
                    <span className="pulse-color-gray-600">Test SMS sent to </span>
                    <span className="pulse-color-blue-500">{smsTested.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
            <div className={styles.addButton}>
              {fields.length < maxRows ? (
                <Button variant="secondary" onClick={addRow}>
                  <Icon icon={FiPlus} /> Add Another
                </Button>
              ) : (
                <div className={styles.info}>To setup additional alerts, go to Notifications &gt; Contacts.</div>
              )}
            </div>
          </div>
          <ButtonRow
            onSubmit={submitStep}
            stepsCount={stepsCount}
            activeStepNr={activeStepNr}
            isNextDisabled={!!errors.rows?.length}
          />
        </form>
      </FormProvider>
    </div>
  );
};
